import { Box, Card, Divider, IconButton, Icons, Stack, Typography } from '@healthinal/ui';
import { useState } from 'react';
import { LabReportDto } from '../../../../../api/generated/model';
import { CardRow } from '../../../../../components/CardRow.tsx';
import { SubCard } from '../../../../../components/SubCard.tsx';
import { formatDateWithGermanMonth } from '../../../../../utils/date.ts';
import {
  getDetectionMethodName,
  getEnzymeName,
  getLabReportMaterialName,
  getMutationStatusName,
  getResistanceName,
  getResistanceNameName,
  getSpeciesNameString,
  getTestResultName,
} from '../../../../../utils/labReport.tsx';
import { FileLink } from '../../../../components/FileLink.tsx';
import { PatientAddressDisplay } from '../../../../components/patient-address/PatientAddressDisplay.tsx';
import { CreateLabReportDialog } from './CreateLabReportDialog.tsx';

export function LabReportCard({ report }: { report: LabReportDto }) {
  const [isEditLabReportDialogOpen, setIsEditLabReportDialogOpen] = useState(false);

  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'start'} gap={3}>
          <Stack flex={2} gap={5}>
            <Stack direction={'row'} gap={2}>
              <SubCard>
                <Icons.Science />
              </SubCard>
              <Stack justifyContent={'space-between'}>
                <Typography level={'h4'}>MLB</Typography>
                <Typography level={'body-sm'}>{formatDateWithGermanMonth(report.samplingDate)}</Typography>
              </Stack>
            </Stack>
            <Stack gap={1}>
              <CardRow title={'Test'} content={getLabReportMaterialName(report)} />
              <Divider />
              <CardRow title={'Nachweismethode'}>
                <Box flex={3}>
                  {report.detectionMethods.map((detectionMethod) => (
                    <Typography
                      key={detectionMethod.method}
                      level={'body-sm'}
                      endDecorator={
                        <Typography level={'title-sm'}>{getTestResultName(detectionMethod.testResult)}</Typography>
                      }>
                      {getDetectionMethodName(detectionMethod.method)}:
                    </Typography>
                  ))}
                </Box>
              </CardRow>
              <Divider />
              <CardRow title={'Spezies'} content={report.species ? getSpeciesNameString(report.species) : ''} />
              <Divider />
              <CardRow title={'Resistenzen'}>
                <Box flex={3}>
                  {report.resistances.map((resistance) => (
                    <Typography
                      key={resistance.resistanceName}
                      level={'body-sm'}
                      endDecorator={
                        <Typography level={'title-sm'}>{getResistanceName(resistance.resistance)}</Typography>
                      }>
                      {getResistanceNameName(resistance.resistanceName)}:
                    </Typography>
                  ))}
                </Box>
              </CardRow>
              <Divider />
              <CardRow title={'Mutationen'}>
                <Box flex={3}>
                  {report.mutations.map((mutation) => (
                    <Typography
                      level={'body-sm'}
                      key={mutation.enzyme}
                      endDecorator={
                        <Typography level={'title-sm'}>{getMutationStatusName(mutation.mutationStatus)}</Typography>
                      }>
                      {getEnzymeName(mutation.enzyme)}:
                    </Typography>
                  ))}
                </Box>
              </CardRow>
              <Divider />
              <CardRow title={'Labor'}>
                {report.labAddress && <PatientAddressDisplay flex={3} patientAddress={report.labAddress} />}
              </CardRow>

              <Divider />
              <CardRow title={'MLB'}>
                {report.fileDetails && <FileLink flex={3} fileDetails={report.fileDetails} />}
              </CardRow>
            </Stack>
          </Stack>
          <Stack flex={1}>
            <Typography level={'title-lg'}>Bemerkungen</Typography>
            <Typography level={'body-sm'} sx={{ whiteSpace: 'pre-line' }}>
              {report.notes}
            </Typography>
          </Stack>
          <IconButton color={'primary'} onClick={() => setIsEditLabReportDialogOpen(true)} title={'MLB bearbeiten'}>
            <Icons.Edit />
          </IconButton>
        </Stack>
      </Card>

      <CreateLabReportDialog
        open={isEditLabReportDialogOpen}
        onClose={() => setIsEditLabReportDialogOpen(false)}
        patientId={report.patientId}
        currentLabReport={report}
      />
    </>
  );
}
